var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { useAsync } from "@iscr/xAppLib/Hooks/useAsync";
import { useParams } from "react-router";
import { Container, Loader } from "semantic-ui-react";
import { useContentfulProvider } from "./useContentful";
import NotFound from "../views/content/NotFound";
import ConditionsTemplate from "./pages/treatment/ConditionsTemplate";
import LandingPage from "./pages/landing_pages/LandingPage";
import { PAGE_IDS } from "./constants";
import { normalizeKeys } from "./functions";
import { BlogPostPreview } from "./pages/blog/blog-post/BlogPostPreview";
import { SEOHeaders } from "@iscr/components/SEOHeaders";
/* Factsheets haven't been properly set up/configured and migrated - https://instant.atlassian.net/browse/INST-5892
They can be omitted for now and could be refactored to use the configurable page templates that are now the best pattern
*/
// import FactSheetTemplate from "./pages/factsheet/FactSheetTemplate";
const ContentMap = {
    [PAGE_IDS.TREATMENT]: ConditionsTemplate,
    [PAGE_IDS.LANDING_PAGE]: LandingPage,
    [PAGE_IDS.BLOG_POST]: BlogPostPreview,
    // factsheet: FactSheetTemplate,
};
const CONTENT_SLUG_MAP = {
    [PAGE_IDS.BLOG_POST]: PAGE_IDS.BLOG_POST,
    [PAGE_IDS.LANDING_PAGE]: PAGE_IDS.LANDING_PAGE,
    [PAGE_IDS.TREATMENT]: PAGE_IDS.TREATMENT,
    [PAGE_IDS.HOMEPAGE]: PAGE_IDS.LANDING_PAGE,
};
const getContentTemplate = (contentTag, pageData) => {
    var _a;
    if (!pageData)
        return ContentMap[contentTag];
    /*
     We can specify the page template in the page data itself. All pages going forward are using the content type landingPage
     Its called a configurable page in contentful - but we can't change the ids.

     The configurable page is very open and flexible (hence the name) - but if we want to specify a specific template for
     a page we can do that here.
    */
    const pageTypeFromData = normalizeKeys((_a = pageData === null || pageData === void 0 ? void 0 : pageData.fields) === null || _a === void 0 ? void 0 : _a.pageType);
    if (pageTypeFromData && ContentMap[pageTypeFromData]) {
        return ContentMap[pageTypeFromData];
    }
    return ContentMap[contentTag];
};
const ContentWrapper = ({ contentTag, identifier, isStatic = false, preview = false, parsedFunctions = {}, }) => {
    var _a, _b;
    const { content_type, "*": splat } = useParams();
    const { getEntry } = useContentfulProvider();
    const contentIdentifier = useMemo(() => identifier || (splat === null || splat === void 0 ? void 0 : splat.replace(/\/+$/, "")), [identifier, splat]);
    const contentTypeId = useMemo(() => (content_type ? CONTENT_SLUG_MAP[content_type] : contentTag), [content_type, contentTag]);
    const preload = useMemo(() => { var _a, _b; return (((_a = window.___) === null || _a === void 0 ? void 0 : _a.loc) == location.pathname && ((_b = window.___) === null || _b === void 0 ? void 0 : _b.content)) || null; }, []);
    const ContentTemplate = useMemo(() => getContentTemplate(contentTypeId), [contentTypeId]);
    const { loading, error, data: { pageData } = { pageData: (preload === null || preload === void 0 ? void 0 : preload.pageData) || null }, } = useAsync({
        immediate: !!contentTypeId,
        fn: () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            if (!contentIdentifier) {
                return Promise.reject(new Error("Page not found"));
            }
            let pageData = null;
            const response = yield getEntry({
                content_type: contentTypeId,
                preview,
                filters: [{ name: "fields.slug", value: contentIdentifier }],
                maxNestedItemDepth: 5,
            });
            if (((_b = (_a = response === null || response === void 0 ? void 0 : response.entries) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.length) > 0) {
                pageData = response.entries.items[0];
            }
            if (pageData) {
                return { pageData };
            }
            else {
                return Promise.reject(new Error("Page not found"));
            }
        }),
    }, [contentIdentifier, contentTypeId, preview]);
    // Show spinner until site state has loaded (i.e. not static?)
    const showLoading = ["weight-loss", "weight-management-program"].includes(identifier !== null && identifier !== void 0 ? identifier : "") && !app.site_status;
    if ((loading && !preload) || showLoading) {
        return (_jsx(Container, { className: "relative h-[300px]", children: _jsx(Loader, { active: true, children: "Loading page..." }) }));
    }
    if (!ContentTemplate) {
        return (_jsx(Container, { children: _jsx("div", { children: "Content type not found" }) }));
    }
    if ((!(pageData === null || pageData === void 0 ? void 0 : pageData.fields) && !preload) || String(error) === "Page not found") {
        return _jsx(NotFound, {});
    }
    if (error && !preload) {
        return (_jsx(Container, { children: _jsx("div", { className: "error-message", children: (_b = (_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : error === null || error === void 0 ? void 0 : error.err) !== null && _b !== void 0 ? _b : String(error) }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(ContentfulHelmet, { pageData: pageData, pageType: contentTypeId }), _jsx(ContentTemplate, { page: pageData, preload: preload, siteStatus: app.site_status, isStatic: isStatic, parsedFunctions: parsedFunctions, isPreview: preview })] }));
};
const getTitle = (fields) => {
    const { metaTitle, title } = fields;
    return metaTitle || title || "";
};
const ContentfulHelmet = ({ pageData }) => {
    if (!pageData)
        return null;
    const { fields } = pageData;
    if (!fields)
        return null;
    const title = getTitle(fields);
    const robots = Array.isArray(fields.metaRobots) ? fields.metaRobots : [];
    return (_jsx(SEOHeaders, { title: title, path: window.location.pathname, metaDescription: undefined, index: !robots.includes('noindex'), follow: !robots.includes('nofollow') }));
};
export default ContentWrapper;
